import React from 'react';
import styled from 'styled-components';
import t from 'modules/i18n/intl';
import config from 'appconfig';
import { FormattedDate, FormattedMessage } from 'react-intl';
import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import AmountWithCurrency from 'components/common/AmountWithCurrency';
import Button from '@atlaskit/button';
import { Dimensions } from 'components/visuals';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Tooltip from 'components/common/Tooltip';
import { colors } from '@atlaskit/theme';

const Cell = styled.td<{ width: number }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px 0;

  && span {
    line-height: unset;
  }
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TooltipContainer = styled.span`
  margin-left: 5px;
  span {
    display: inline-flex;
  }
`;

type InvoicesActionLinkType = {
  label: any;
  href?: string;
  disabled?: boolean;
  onClick?: (e: any) => void;
};

const bookedInvoiceStatuses = ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'];

const getInvoicesActions = (
  rowItem: any,
  handleJSONDownload: (e: any, orderId: string) => void,
  handleInvoiceDownload: (e: any, orderId: string) => void,
): InvoicesActionLinkType[] => {
  const { orderId, processInvoice, status } = rowItem;
  const links: InvoicesActionLinkType[] = [];
  const shouldShowInvoiceLink = !(!processInvoice && !bookedInvoiceStatuses.includes(status));

  links.push({
    label: 'JSON',
    onClick: (e) => handleJSONDownload(e, orderId),
  });

  if (shouldShowInvoiceLink) {
    links.push({
      label: t('mac.orders.table.table-item.action.invoice'),
      onClick: (e) => handleInvoiceDownload(e, orderId),
    });
  }

  return links;
};

const StatusTooltip = () => (
  <TooltipContainer>
    <Tooltip
      direction="right"
      content={
        <FormattedMessage
          id="mac.invoices.table.table-item.status.tooltip.text"
          values={{
            link: (
              <a href={`${config.wacUrl}/my`}>
                {t('mac.invoices.table.table-item.status.tooltip.link')}
              </a>
            ),
          }}
        />
      }
    >
      <QuestionCircleIcon
        label={t('mac.invoices.table.table-item.status.tooltip.link')}
        size="small"
        primaryColor={colors.DN200}
      />
    </Tooltip>
  </TooltipContainer>
);

export const InvoiceCollapsedRow = ({
  showContent,
  rowItem,
  handleJSONDownload,
  handleInvoiceDownload,
}: {
  showContent: boolean;
  rowItem: any;
  handleJSONDownload: (e: any, orderId: string) => void;
  handleInvoiceDownload: (e: any, orderId: string) => void;
}) => {
  const { cartOrderDetails, orderNumber, status } = rowItem;
  const createdDate = new Date(cartOrderDetails?.created);
  const links = getInvoicesActions(rowItem, handleJSONDownload, handleInvoiceDownload);

  return (
    <>
      <Cell width={15} key="invoices-row-sen">
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          {orderNumber}
        </ChevronCellWrapper>
      </Cell>
      <Cell width={15} key="invoices-row-created">
        <FormattedDate value={createdDate} year="numeric" month="short" day="2-digit" />
      </Cell>
      <Cell width={15} key="invoices-row-total">
        <AmountWithCurrency
          amount={cartOrderDetails?.totalIncTax}
          currency={cartOrderDetails?.currency || 'USD'}
          weight="400"
        />
      </Cell>
      <Cell width={15} key="invoices-row-status">
        {status} <StatusTooltip />
      </Cell>
      <Cell width={15} key="invoices-row-actions">
        {links.map((link) => (
          <Button
            appearance="link"
            href={link.href}
            key={link.label}
            isDisabled={link.disabled}
            onClick={link.onClick}
          >
            {link.label}
          </Button>
        ))}
      </Cell>
    </>
  );
};
